import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import apiClient from "@shared/services/apiClient";
import {
  ReportPackageResponse,
  ReportSnapshot,
  SnapshotDraft,
} from "./customizationTypes";
import {
  getErrorMessage,
  parseJsonObject,
  toDateTimeLabel,
  unwrapApiData,
} from "./customizationUtils";
import { useMontageOptions } from "./customizationHooks";

type ReportSnapshotEditorPanelProps = {
  reportId: string;
};

const ReportSnapshotEditorPanel: React.FC<ReportSnapshotEditorPanelProps> = ({
  reportId,
}) => {
  const [rows, setRows] = useState<ReportSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, SnapshotDraft>>({});
  const [timestamp, setTimestamp] = useState("");
  const [caption, setCaption] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [montageId, setMontageId] = useState("");
  const { options: montageOptions, loading: montagesLoading } =
    useMontageOptions();
  const [filterStateText, setFilterStateText] = useState("{}");
  const [annotationIds, setAnnotationIds] = useState("");
  const [reportPackage, setReportPackage] =
    useState<ReportPackageResponse | null>(null);

  const loadSnapshots = useCallback(async () => {
    if (!reportId.trim()) {
      setRows([]);
      setDrafts({});
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/reports/${reportId.trim()}/snapshots`,
      );
      const data = unwrapApiData<ReportSnapshot[]>(response);
      const nextRows = Array.isArray(data) ? data : [];
      setRows(nextRows);
      const nextDrafts: Record<string, SnapshotDraft> = {};
      nextRows.forEach((row) => {
        nextDrafts[row.snapshot_id] = {
          caption: row.caption || "",
          displayOrder: row.display_order,
        };
      });
      setDrafts(nextDrafts);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const addSnapshot = async () => {
    if (!reportId.trim()) {
      toast.error("Report ID is required.");
      return;
    }
    const parsedTimestamp = Number(timestamp);
    if (Number.isNaN(parsedTimestamp) || parsedTimestamp < 0) {
      toast.error("Timestamp must be a non-negative number.");
      return;
    }
    const parsedDisplayOrder = displayOrder.trim()
      ? Number(displayOrder)
      : undefined;
    if (parsedDisplayOrder !== undefined && Number.isNaN(parsedDisplayOrder)) {
      toast.error("Display order must be a number.");
      return;
    }
    try {
      const filterState = parseJsonObject(filterStateText, "Filter state");
      const parsedAnnotationIds = annotationIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
      await apiClient.post(`/reports/${reportId.trim()}/snapshots`, {
        timestamp_in_recording: parsedTimestamp,
        caption: caption.trim() || undefined,
        display_order: parsedDisplayOrder,
        montage_id: montageId.trim() || undefined,
        filter_state_json: filterState,
        annotation_ids: parsedAnnotationIds,
      });
      toast.success("Snapshot added.");
      setTimestamp("");
      setCaption("");
      setDisplayOrder("");
      setMontageId("");
      setFilterStateText("{}");
      setAnnotationIds("");
      await loadSnapshots();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveSnapshot = async (snapshotId: string) => {
    if (!reportId.trim()) return;
    const draft = drafts[snapshotId];
    if (!draft) return;
    try {
      await apiClient.put(
        `/reports/${reportId.trim()}/snapshots/${snapshotId}`,
        {
          caption: draft.caption,
          display_order: draft.displayOrder,
        },
      );
      toast.success("Snapshot updated.");
      await loadSnapshots();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteSnapshot = async (snapshotId: string) => {
    if (!reportId.trim()) return;
    try {
      await apiClient.delete(
        `/reports/${reportId.trim()}/snapshots/${snapshotId}`,
      );
      toast.success("Snapshot deleted.");
      await loadSnapshots();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const generatePackage = async () => {
    if (!reportId.trim()) {
      toast.error("Report ID is required.");
      return;
    }
    try {
      const response = await apiClient.post(
        `/reports/${reportId.trim()}/generate-package`,
      );
      setReportPackage(unwrapApiData<ReportPackageResponse>(response));
      toast.success("Report package generated.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Card>
      <Card.Body>
        <h5><Trans ns="features" i18nKey="ui.copy.a0c2df9473ab" /></h5>
        <p className="text-muted">
          <Trans ns="features" i18nKey="ui.copy.482fcef6aec9" /></p>
        {!reportId.trim() && (
          <Alert variant="warning">
            <Trans ns="features" i18nKey="ui.copy.1ecdbf7b6178" /></Alert>
        )}
        <Row className="g-3 mb-3">
          <Col md={2}>
            <Form.Control
              value={timestamp}
              onChange={(event) => setTimestamp(event.target.value)}
              placeholder="Timestamp sec"
            />
          </Col>
          <Col md={2}>
            <Form.Control
              value={displayOrder}
              onChange={(event) => setDisplayOrder(event.target.value)}
              placeholder="Display order"
            />
          </Col>
          <Col md={3}>
            <Form.Select
              value={montageId}
              onChange={(event) => setMontageId(event.target.value)}
              disabled={montagesLoading}
            >
              <option value="">
                {montagesLoading ? "Loading…" : "— No montage —"}
              </option>
              {montageOptions.map((m) => (
                <option key={m.montage_id} value={m.montage_id}>
                  {m.montage_name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={5}>
            <Form.Control
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
              placeholder="Caption"
            />
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Label className="small text-muted">
              <Trans ns="features" i18nKey="ui.copy.c4b7c64644f8" /></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="json-editor"
              value={filterStateText}
              onChange={(event) => setFilterStateText(event.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small text-muted">
              <Trans ns="features" i18nKey="ui.copy.8792cf57c4d5" /></Form.Label>
            <Form.Control
              value={annotationIds}
              onChange={(event) => setAnnotationIds(event.target.value)}
            />
          </Col>
          <Col md={2} className="d-grid align-items-end">
            <Button onClick={addSnapshot} disabled={!reportId.trim()}>
              <Trans ns="features" i18nKey="ui.copy.1ce63771aa76" /></Button>
          </Col>
        </Row>

        <div className="d-flex gap-2 mb-3">
          <Button
            variant="outline-secondary"
            onClick={loadSnapshots}
            disabled={!reportId.trim()}
          >
            <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" /></Button>
          <Button
            variant="outline-primary"
            onClick={generatePackage}
            disabled={!reportId.trim()}
          >
            <Trans ns="features" i18nKey="ui.copy.d9685a47da09" /></Button>
        </div>
        {reportPackage && (
          <Alert variant="success">
            <div>
              <strong><Trans ns="features" i18nKey="ui.copy.554467740d4b" /></strong> {reportPackage.package.report_id}
            </div>
            <div>
              <strong><Trans ns="features" i18nKey="ui.copy.63fce3b5e21c" /></strong>{" "}
              {reportPackage.package.snapshot_count}
            </div>
            <div>
              <strong><Trans ns="features" i18nKey="ui.copy.a2edf57ce93f" /></strong>{" "}
              {toDateTimeLabel(reportPackage.package.generated_at)}
            </div>
            <div>
              <strong><Trans ns="features" i18nKey="ui.copy.40bd03ed703b" /></strong> {reportPackage.pdf_status}
            </div>
            <div className="small">{reportPackage.message}</div>
          </Alert>
        )}

        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : rows.length === 0 ? (
          <Alert variant="light" className="mb-0">
            <Trans ns="features" i18nKey="ui.copy.c213816962e1" /></Alert>
        ) : (
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th><Trans ns="features" i18nKey="ui.copy.19eabc961735" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.3428d97e6666" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.1d75774c0f96" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.7db11d819855" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.c3cd636a585b" /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.snapshot_id}>
                  <td>{row.timestamp_in_recording}</td>
                  <td>
                    <Form.Control
                      value={drafts[row.snapshot_id]?.caption || ""}
                      onChange={(event) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [row.snapshot_id]: {
                            ...(prev[row.snapshot_id] || {
                              caption: row.caption || "",
                              displayOrder: row.display_order,
                            }),
                            caption: event.target.value,
                          },
                        }))
                      }
                    />
                  </td>
                  <td style={{ width: 140 }}>
                    <Form.Control
                      type="number"
                      value={
                        drafts[row.snapshot_id]?.displayOrder ??
                        row.display_order
                      }
                      onChange={(event) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [row.snapshot_id]: {
                            ...(prev[row.snapshot_id] || {
                              caption: row.caption || "",
                              displayOrder: row.display_order,
                            }),
                            displayOrder: Number(event.target.value),
                          },
                        }))
                      }
                    />
                  </td>
                  <td>{row.montage_id || "-"}</td>
                  <td className="customization-actions-cell">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => saveSnapshot(row.snapshot_id)}
                    >
                      <Trans ns="features" i18nKey="ui.copy.efc007a393f6" /></Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => deleteSnapshot(row.snapshot_id)}
                    >
                      <Trans ns="features" i18nKey="ui.copy.f6fdbe48dc54" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default ReportSnapshotEditorPanel;
